import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import Table, { Column } from '../components/Table';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function LetterEditor() {
  const [templates, setTemplates] = useState<any[]>([]);
  const [sent, setSent] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ templateName: '', templateContent: '', letterheadHtml: '' });
  const [sendTarget, setSendTarget] = useState<any>(null);
  const [sendForm, setSendForm] = useState({ employeeId: '', subject: '', sendEmail: true });

  const fetchAll = async () => {
    const [t, s] = await Promise.all([api.get('/hr/letters/templates'), api.get('/hr/letters/sent')]);
    setTemplates(t.data.templates || []);
    setSent(s.data.letters || []);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const createTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post('/hr/letters/templates', form);
    setForm({ templateName: '', templateContent: '', letterheadHtml: '' });
    setShowForm(false);
    fetchAll();
  };

  const sendLetter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sendTarget) return;
    try {
      await api.post('/hr/letters/send', { templateId: sendTarget.id, ...sendForm });
      setSendTarget(null);
      fetchAll();
      alert('Letter sent');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send');
    }
  };

  const sentColumns: Column<any>[] = [
    { key: 'employee_name', header: 'Employee' },
    { key: 'subject', header: 'Subject' },
    { key: 'emailed', header: 'Emailed', render: (r) => (r.emailed ? 'Yes' : 'No') },
    { key: 'sent_at', header: 'Sent', render: (r) => new Date(r.sent_at).toLocaleString() },
  ];

  return (
    <div className="p-6 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Letter Editor</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'New Template'}</Button>
      </div>

      {showForm && (
        <form onSubmit={createTemplate} className="bg-white rounded-lg shadow p-6 space-y-4">
          <Input label="Template Name" value={form.templateName} onChange={(e) => setForm({ ...form, templateName: e.target.value })} required />
          <textarea
            className="w-full border rounded-lg px-3 py-2"
            rows={2}
            placeholder="Letterhead HTML (optional)"
            value={form.letterheadHtml}
            onChange={(e) => setForm({ ...form, letterheadHtml: e.target.value })}
          />
          <textarea
            className="w-full border rounded-lg px-3 py-2"
            rows={8}
            placeholder="Template content. Use {{employeeName}}, {{employeeCode}}, {{designation}}, {{department}}, {{companyName}}, {{date}}"
            value={form.templateContent}
            onChange={(e) => setForm({ ...form, templateContent: e.target.value })}
            required
          />
          <Button type="submit">Save Template</Button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="bg-white rounded-lg shadow p-4">
            <p className="font-semibold mb-2">{t.template_name}</p>
            <button className="text-blue-600 hover:underline text-sm" onClick={() => setSendTarget(t)}>
              Send to Employee
            </button>
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Sent Letters</h2>
        <Table columns={sentColumns} rows={sent} emptyMessage="No letters sent yet" />
      </div>

      <Modal isOpen={!!sendTarget} onClose={() => setSendTarget(null)} title={`Send "${sendTarget?.template_name}"`}>
        <form onSubmit={sendLetter} className="space-y-4">
          <LookupSelect label="Employee" value={sendForm.employeeId} onChange={(v) => setSendForm({ ...sendForm, employeeId: v })} fetchOptions={fetchEmployeeOptions} required />
          <Input label="Subject" value={sendForm.subject} onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sendForm.sendEmail} onChange={(e) => setSendForm({ ...sendForm, sendEmail: e.target.checked })} />
            Also email the employee
          </label>
          <Button type="submit" className="w-full">
            Send
          </Button>
        </form>
      </Modal>
    </div>
  );
}
